import React from 'react'
import moment from 'moment'

import OrderActions from '../../actions/OrderActions.js'
import {withVehiclesHook} from '../../stores/VehicleStore.js'

import {H6, IconButton, Colors, P, ListItem, Icon, SortableList, S1, Caption, Popover, Card, Popup, Button, ToolTip, Badge, Divider, Row, Stack} from '../UI/index.js'
import {formSpacing, ColorPicker, Recurrence} from '../UI/forms/index.js'
import time from '../../../server/utils/time.js'
import _ from 'lodash'
import {formatDistance, formatDuration} from '../../../utils/utils.js'
import {formatPrice} from '../../../utils/price.js'
import stopBundles from '../../../utils/stopBundles.js'
import Languages from '@utils/Languages.js'
import t from 'src/helpers/Languages.js'
import {isAnchorLine, getStopItems, getDraggedAddresses} from './routeStopItems.js'
import {getRouteTimeSlots} from './routeSummary.js'
import {isRetourOrder} from '../../../utils/retourAddresses.js'
import branchesAvailable from '@utils/branchesAvailable.js'
import {navOffset} from '../navigation/navPosition.js'
import NextStopETA from './NextStopETA.js'

class RoutePanel extends React.PureComponent {
    constructor(props) {
        super(props)

        this.state = {
            routeInfoExpanded: localStorage.getItem('routeInfoExpanded') === 'true',
            expandedBundles: {}
        }
    }

    componentDidUpdate(prevProps) {
        if (this.props.route !== prevProps.route && this.props.route) {
            console.log(this.props.route)
        }
    }

    toggleRouteInfoExpanded() {
        const expanded = !this.state.routeInfoExpanded
        this.setState({routeInfoExpanded: expanded})
        localStorage.setItem('routeInfoExpanded', expanded)
    }

    isAddressToLate(date, address) {
        return !address.hasArrived && moment(date).hour(address.endTime.split(':')[0]).minute(address.endTime.split(':')[1]).diff(moment(), 'minutes') < 10
    }

    isOrderDisrupted(order, addresses, draggedAddress, newIndex) {
        const {route} = this.props

        // Find the current index of the dragged address in the route
        const currentDraggedAddressIndex = route.addresses.findIndex((address) => address.orderId === draggedAddress.orderId && address.index === draggedAddress.index)

        return order.addresses.some((address) => {
            if (address.index === draggedAddress.index) {
                return false
            }

            // Check if the dragged address is before or after the current address in the order
            const draggedAddressIsBeforeInOrder = draggedAddress.index < address.index
            const draggedAddressIsAfterInOrder = draggedAddress.index > address.index

            // Find the old and new index of the current address in the route
            const addressRouteOldIndex = route.addresses.findIndex((routeAddress) => routeAddress.orderId === order._id && routeAddress.index === address.index)
            const addressRouteIndex = addresses.findIndex((routeAddress) => routeAddress.orderId === order._id && routeAddress.index === address.index)

            if (addressRouteIndex === -1) {
                return false
            }

            // Check if the dragged address is before or after the current address in the new route
            const draggedAddressIsBeforeInRoute = newIndex < addressRouteIndex
            const draggedAddressIsAfterInRoute = newIndex > addressRouteIndex

            // Check if the dragged address was before or after the current address in the old route
            const draggedAddressWasBeforeInRoute = currentDraggedAddressIndex < addressRouteOldIndex
            const draggedAddressWasAfterInRoute = currentDraggedAddressIndex > addressRouteOldIndex

            if (draggedAddressIsBeforeInOrder && draggedAddressIsAfterInRoute) {
                return draggedAddressWasBeforeInRoute
            }

            if (draggedAddressIsAfterInOrder && draggedAddressIsBeforeInRoute) {
                return draggedAddressWasAfterInRoute
            }
        })
    }

    async onDragChange(items, newIndex) {
        const {route, orders} = this.props

        // A drag that started before the route was loading
        if (route?.loading) {
            return
        }

        const draggedItem = items[newIndex]
        const addresses = getDraggedAddresses(items, newIndex, this.state.expandedBundles, route?.anchorsEnabled)

        if (!isAnchorLine(draggedItem)) {
            const draggedAddresses = draggedItem.isBundle ? draggedItem.addresses : [draggedItem]

            const orderDisrupted = draggedAddresses.some((draggedAddress) => {
                const order = orders[draggedAddress.orderId]
                const addressIndex = addresses.findIndex((address) => address.id === draggedAddress.id)

                return order?.addressOrder === 'fixed' && this.isOrderDisrupted(order, addresses, draggedAddress, addressIndex)
            })

            if (orderDisrupted) {
                if (!await this.popup.open(t('deviateFromFixedOrder'), t('deviateFromFixedOrderConfirm'))) return
                this.popup.close()
            }
        }

        OrderActions.planRoute(route._id, false, false, addresses)
    }

    // Whether the (expected) arrival is before or after the time slot of the address, and the durations to show
    getTimeSlotInfo(address) {
        let beforeTimeSlot = false
        let afterTimeSlot = false

        if (address.estimatedTimeOfArrival) {
            afterTimeSlot = moment(address.estimatedTimeOfArrival, 'HH:mm').isAfter(moment(address.endTime, 'HH:mm'))
        } else if (typeof address.duration === 'number') {
            afterTimeSlot = moment().add(address.duration || 0, 'seconds').isAfter(moment(address.endTime, 'HH:mm'))
        }

        if (address.timeOfArrival) {
            afterTimeSlot = moment(address.timeOfArrival, 'HH:mm').isAfter(moment(address.endTime, 'HH:mm'))
        }

        if (!afterTimeSlot) {
            if (address.estimatedTimeOfArrival) {
                beforeTimeSlot = moment(address.estimatedTimeOfArrival, 'HH:mm').isBefore(moment(address.startTime, 'HH:mm'))
            } else if (typeof address.duration === 'number') {
                beforeTimeSlot = moment().add(address.duration || 0, 'seconds').isBefore(moment(address.startTime, 'HH:mm'))
            }

            if (address.timeOfArrival) {
                beforeTimeSlot = moment(address.timeOfArrival, 'HH:mm').isBefore(moment(address.startTime, 'HH:mm'))
            }
        }

        const inTimeSlot = !beforeTimeSlot && !afterTimeSlot

        let diff
        let formattedDiff = ''

        if (address.timeOfArrival && address.plannedTimeOfArrival) {
            diff = moment(address.timeOfArrival, 'HH:mm').diff(moment(address.plannedTimeOfArrival, 'HH:mm'), 'seconds')
            formattedDiff = formatDuration(diff, true)
        }

        let formattedDuration = ''

        if (typeof address.duration === 'number' && !address.estimatedTimeOfArrival) {
            formattedDuration = formatDuration(address.duration)
        }

        return {beforeTimeSlot, afterTimeSlot, inTimeSlot, formattedDiff, formattedDuration}
    }

    renderTimes(address) {
        const {beforeTimeSlot, afterTimeSlot, inTimeSlot, formattedDiff, formattedDuration} = this.getTimeSlotInfo(address)

        return (
            <>
                {!address.hasArrived && formattedDuration &&
                    <>
                        <Icon
                            size='md'
                            style={{marginRight: 6, marginLeft: 24, color: afterTimeSlot ? Colors.errorBright : undefined}}
                            icon={inTimeSlot ? 'mdi mdi-timer-sand' : 'mdi mdi-timer-sand-complete'}
                        />
                        <P>{formattedDuration}</P>
                    </>
                }

                {!address.hasArrived && address.estimatedTimeOfArrival &&
                    <ToolTip text={t('expectedTimeOfArrival')}>
                        <Row style={{flexShrink: 0, marginLeft: 24}}>
                            <Icon
                                size='md'
                                style={{marginRight: 6, color: afterTimeSlot ? Colors.errorBright : beforeTimeSlot ? Colors.warningBright : undefined}}
                                icon={inTimeSlot ? 'mdi mdi-clock-outline' : 'mdi mdi-clock-alert-outline'}
                            />
                            <P>{address.estimatedTimeOfArrival}</P>
                        </Row>
                    </ToolTip>
                }

                {address.hasArrived &&
                    <ToolTip text={address.completedByReseller ? t('calculatedEtaCompletedByPlanner') : t('timeOfArrival')} placement='left'>
                        <Row style={{flexShrink: 0, marginLeft: 24}}>
                            {!address.completedByReseller && (
                                <Icon
                                    size='md'
                                    style={{marginRight: 6, color: afterTimeSlot ? Colors.errorBright : beforeTimeSlot ? Colors.warningBright : undefined}}
                                    icon={inTimeSlot ? 'mdi mdi-clock-check-outline' : 'mdi mdi-clock-alert-outline'}
                                />
                            )}

                            <P style={address.completedByReseller ? {color: Colors.textMedium} : undefined}>{address.timeOfArrival}</P>
                        </Row>
                    </ToolTip>
                }

                {formattedDiff && !address.completedByReseller &&
                    <ToolTip text={t('etaDeviation')}>
                        <P style={{marginLeft: 24}}>{formattedDiff}</P>
                    </ToolTip>
                }
            </>
        )
    }

    // Locks or unlocks the stop numbers of the given stops, for a bundle these are all its stops
    async setLocked(addresses, locked) {
        const {route} = this.props

        if (route.ready && !locked) {
            if (!await this.popup.open(t('changeRoute'), t('unlockReadyRouteConfirm'))) return

            await new Promise((resolve) => {
                OrderActions.setRouteReady(route._id, false, (err) => {
                    if (err) {
                        this.popup.setError(err)
                    } else {
                        resolve()
                    }
                })
            })
        }

        const errors = await Promise.all(addresses.map((address) => new Promise((resolve) => {
            OrderActions.setAddressLocked(address.orderId, address.index, locked, resolve)
        })))

        const err = errors.find((err) => err)

        if (err) {
            this.popup.setError(err)
        } else {
            this.popup.close()
        }
    }

    renderStop(address) {
        const {route, viewOrder, reseller} = this.props

        const order = this.props.orders?.[address.orderId]
        const isRetour = !!order && isRetourOrder(order)
        const originalAddress = isRetour ? order.originalAddresses?.[0] : undefined

        return (
            <Card
                shadow='true'
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: 0,
                    marginBottom: 6,
                    // Stops of an opened bundle are indented below the bundle
                    marginLeft: address.bundleId ? 24 : 0,
                    width: address.bundleId ? 'calc(100% - 24px)' : '100%',
                    position: 'relative'
                }}
                onClick={() => viewOrder(address.orderId, address.index)}
            >
                {address.anchor &&
                    <Icon
                        size='md'
                        style={{marginRight: 6, position: 'absolute', right: 6, bottom: 3, color: Colors.textDark, cursor: 'inherit'}}
                        icon='mdi mdi-anchor'
                    />
                }

                <Stack style={{width: 36, alignItems: 'center', justifyContent: 'space-between'}}>
                    {address.stopNumber &&
                        <S1>{address.stopNumber}</S1>
                    }

                    {!address.hasArrived && address.stopNumber &&
                        <IconButton
                            icon={address.locked ? 'mdi mdi-lock' : 'mdi mdi-lock-open-variant-outline'}
                            onClick={(event) => {
                                event.stopPropagation()

                                this.setLocked([address], !address.locked)
                            }}
                        />
                    }
                </Stack>

                <div
                    style={{
                        background: isRetour ? Colors.retour : Colors[address.type],
                        opacity: address.isExpected ? 0.5 : 1,
                        width: 4,
                        alignSelf: 'stretch'
                    }}
                />

                <Stack style={{flex: 1, overflow: 'hidden', paddingLeft: 6, paddingRight: 6}}>
                    <Row style={{flexShrink: 0, alignItems: 'center', height: 24}}>

                        <Caption ellipsis style={{flex: 1}}>{address.senderAddress?.name || address.customerAddress?.name}</Caption>

                        {!address.hasArrived &&
                            <Caption ellipsis>{`${address.startTime} - ${address.endTime}`}</Caption>
                        }

                        {this.renderTimes(address)}

                        {route.started && !address.underway && !address.hasArrived &&
                            <Badge variant='warning' style={{marginLeft: 6, marginTop: 3}}>{t('notStarted')}</Badge>
                        }
                    </Row>

                    <Row style={{flexShrink: 0, alignItems: 'center', height: 24}}>
                        {!address.position &&
                            <Icon style={{marginRight: 6}} size='md' icon='mdi mdi-alert' />
                        }

                        <P ellipsis style={{flex: 1}}>{`${address.street} ${address.nr}${address.addition} ${address.postalCode} ${address.city}`}</P>

                        {address.hasArrived &&
                            <Icon
                                size='sm'
                                style={{marginRight: 6, width: 24, color: address.success ? Colors.successBright : Colors.warningBright}}
                                icon='mdi mdi-circle'
                            />
                        }
                    </Row>

                    <Row style={{flexShrink: 0, alignItems: 'center', height: 21}}>
                        <Icon size='md' style={{marginRight: 6, color: Colors.textMedium}} icon={address.isBusiness ? 'mdi mdi-store-outline' : 'mdi mdi-account'} />
                        <Caption ellipsis style={{flex: 1}}>{address.name}</Caption>
                    </Row>

                    {originalAddress &&
                        <Row style={{flexShrink: 0, alignItems: 'center', height: 21, opacity: 0.6}}>
                            <Caption ellipsis style={{flex: 1}}>{`${originalAddress.name ? `${originalAddress.name}, ` : ''}${originalAddress.street} ${originalAddress.nr}${originalAddress.addition} ${originalAddress.city}`}</Caption>
                        </Row>
                    }

                    {reseller.settings.orders.showOptionsInPlanning === 'yes' && (
                        <div style={{display: 'flex', alignItems: 'center'}}>
                            {address.gpsRequired &&
                                <Icon style={{fontSize: 20, marginRight: 3}} icon='mdi mdi-map-marker' />
                            }
                            {address.signatureRequired &&
                                <Icon style={{fontSize: 20, marginRight: 3}} icon='mdi mdi-fountain-pen-tip' />
                            }
                            {address.nameRequired && !address.signatureRequired &&
                                <Icon style={{fontSize: 20, marginRight: 3}} icon='mdi mdi-signature-text' />
                            }
                            {address.signatureNeighbors &&
                                <Icon style={{fontSize: 20, marginRight: 3}} icon='mdi mdi-home-edit-outline' />
                            }
                            {address.statedAddressOnly &&
                                <Icon style={{fontSize: 20, marginRight: 3}} icon='mdi mdi-home' />
                            }
                            {address.mailboxDelivery &&
                                <Icon style={{fontSize: 20, marginRight: 3}} icon='mdi mdi-mailbox-open-up' />
                            }
                            {address.refrigerated &&
                                <Icon style={{fontSize: 20, marginRight: 3}} icon='mdi mdi-snowflake' />
                            }
                            {address.scanRequired &&
                                <Icon style={{fontSize: 20, marginRight: 3}} icon='mdi mdi-barcode-scan' />
                            }
                            {address.imageRequired &&
                                <Icon style={{marginRight: 6, fontSize: 20}} icon='mdi mdi-camera' />
                            }
                            {address.minimumAge &&
                                <div style={{fontSize: 14, fontWeight: 500, marginRight: 6}}>{`${address.minimumAge}+`}</div>
                            }
                            {address.idCheck &&
                                <Icon style={{fontSize: 20, marginRight: 3}} icon='mdi mdi-card-account-details-outline' />
                            }
                            {address.verificationCode &&
                                <Icon style={{marginRight: 6, fontSize: 20}} icon='mdi mdi-shield-key-outline' />
                            }
                        </div>
                    )}

                    {reseller.settings.orders.showOptionsInPlanning !== 'no' && address.customOptions?.length > 0 && (
                        <Row style={{flexShrink: 0, alignItems: 'center', paddingBottom: 2, gap: 4, flexWrap: 'wrap'}}>
                            {address.customOptions.map((option) => {
                                const optionObj = reseller.settings.orders.customOptions?.find((o) => o.id === option)

                                return optionObj ? <Badge key={optionObj.id} size='sm' color={optionObj.color}>{optionObj.name}</Badge> : null
                            })}
                        </Row>
                    )}
                </Stack>
            </Card>
        )
    }

    toggleBundle(bundleId) {
        this.setState({expandedBundles: {...this.state.expandedBundles, [bundleId]: !this.state.expandedBundles[bundleId]}})
    }

    renderBundle(bundle) {
        const firstAddress = bundle.addresses[0]
        const expanded = !!this.state.expandedBundles[bundle.id]

        return (
            <Card
                shadow='true'
                style={{display: 'flex', alignItems: 'center', padding: 0, marginBottom: 6, width: '100%', position: 'relative'}}
                onClick={() => this.toggleBundle(bundle.id)}
            >
                {bundle.anchor &&
                    <Icon
                        size='md'
                        style={{marginRight: 6, position: 'absolute', right: 6, bottom: 3, color: Colors.textDark, cursor: 'inherit'}}
                        icon='mdi mdi-anchor'
                    />
                }

                <Stack style={{width: 36, alignItems: 'center', justifyContent: 'space-between'}}>
                    <S1>{firstAddress.stopNumber}</S1>

                    {!bundle.hasArrived &&
                        <IconButton
                            icon={bundle.locked ? 'mdi mdi-lock' : 'mdi mdi-lock-open-variant-outline'}
                            onClick={(event) => {
                                event.stopPropagation()

                                this.setLocked(bundle.addresses, !bundle.locked)
                            }}
                        />
                    }
                </Stack>

                <div
                    style={{
                        background: Colors[bundle.type],
                        opacity: bundle.addresses.every((address) => address.isExpected) ? 0.5 : 1,
                        width: 4,
                        alignSelf: 'stretch'
                    }}
                />

                <Stack style={{flex: 1, overflow: 'hidden', paddingLeft: 6, paddingRight: 6}}>
                    <Row style={{flexShrink: 0, alignItems: 'center', height: 24}}>
                        <Badge size='sm' style={{fontWeight: 'bold'}} color={bundle.type === 'pickup' ? Colors.pickupLight : Colors.deliveryLight}>{t('bundleStops', bundle.addresses.length)}</Badge>

                        <div style={{flex: 1}} />

                        {this.renderTimes(firstAddress)}
                    </Row>

                    <Row style={{flexShrink: 0, alignItems: 'center', height: 24}}>
                        {!firstAddress.position &&
                            <Icon style={{marginRight: 6}} size='md' icon='mdi mdi-alert' />
                        }

                        <P ellipsis style={{flex: 1}}>{`${firstAddress.street} ${firstAddress.nr}${firstAddress.addition} ${firstAddress.postalCode} ${firstAddress.city}`}</P>

                        {bundle.hasArrived &&
                            <Icon
                                size='sm'
                                style={{marginRight: 6, width: 24, color: bundle.addresses.every((address) => address.success) ? Colors.successBright : Colors.warningBright}}
                                icon='mdi mdi-circle'
                            />
                        }
                    </Row>

                    <Row style={{flexShrink: 0, alignItems: 'center', height: 21, cursor: 'pointer'}}>
                        <Icon size='md' style={{marginRight: 6, color: Colors.textMedium}} icon={expanded ? 'mdi mdi-chevron-up' : 'mdi mdi-chevron-down'} />
                        <Caption>{expanded ? t('hideStops') : t('showStops')}</Caption>
                    </Row>
                </Stack>
            </Card>
        )
    }

    render() {
        const {usersObject, route, focusedRouteId, onClose, vehicles, reseller, routes} = this.props

        route?.addresses?.map((address) => {
            if (this.isAddressToLate(route?.date, address)) {
                address.toLate = true
            }
        })

        const uniqueColli = route?.colli?.filter((c, index, self) => c.barcode ? index === self.findIndex((collo) => collo.barcode === c.barcode) : true)

        const amountOfColli = uniqueColli?.length || 0

        const vehicle = vehicles.find((vehicle) => vehicle._id === route?.vehicleId)
        const vehicleType = reseller.settings.vehicleTypes.find((vehicleType) => vehicleType._id === route?.vehicleTypeId)
        const vehicleName = vehicle?.name || vehicleType?.name || ''

        const stopItems = getStopItems(route, this.state.expandedBundles)

        let actualDurationSec = 0
        let delay = 0

        if (route?.startTime && route?.endTime) {
            const start = moment(route.startTime).startOf('minute')
            let end = moment(route.endTime).startOf('minute')

            if (start.isValid() && end.isValid()) {
                actualDurationSec = end.diff(start, 'seconds')
                delay = actualDurationSec - (route?.plannedDuration || 0)
            }
        }

        return (
            <Stack style={{flex: 1,
                maxHeight: `calc(100vh - ${125 + navOffset().top}px)`,
                width: '100%',
                borderLeft: `${route ? 1 : 0}px solid ${Colors.grey40}`
            }}
            >
                <div style={{flex: 1, overflowY: 'auto', width: '100%', padding: 6}}>

                    <Card
                        shadow={this.state.routeInfoExpanded}
                        style={{
                            background: this.state.routeInfoExpanded ? 'white' : 'transparent',
                            border: 'none',
                            width: '100%',
                            marginBottom: 12,
                            height: 'fit-content',
                            padding: 12
                        }}
                    >
                        <Row style={{flexShrink: 0}}>
                            <Row style={{flexShrink: 0, justifyContent: 'center', alignItems: 'center', flex: 4, marginLeft: 36}}>
                                <ColorPicker
                                    value={route?.color}
                                    colors={Colors.routeColors}
                                    onChange={(event) => OrderActions.updateRoute(route?._id, {color: event.target.value})}
                                />

                                <H6 style={{marginBottom: 0, maxWidth: 240}}>{route?.name || ''}</H6>

                                {(route?.plannedStartTime || route?.startTime) &&
                                    <H6 style={{marginBottom: 0, marginLeft: 6}}>{`${route?.plannedStartTime || route.startTime ? ` (${moment(route?.plannedStartTime ? `${route.date} ${route?.plannedStartTime}` : route.startTime).format('HH:mm')})` : ''}`}</H6>
                                }

                                <IconButton
                                    icon={this.state.routeInfoExpanded ? 'mdi mdi-chevron-up' : 'mdi mdi-chevron-down'}
                                    onClick={() => this.toggleRouteInfoExpanded()}
                                />
                            </Row>

                            <IconButton
                                icon='mdi mdi-close'
                                onClick={onClose}
                            />
                        </Row>

                        {this.state.routeInfoExpanded &&
                            <>
                                <Row style={{flexShrink: 0}}>
                                    <Stack style={{flex: 1}}>
                                        <P>{`${Languages.messenger['NL']}: ${usersObject[route?.messengerId]?.name || '-'}`}</P>

                                        <Row style={{flexShrink: 0}}>
                                            <P>{`${Languages.vehicle['NL']}: ${vehicleName}`}</P>
                                            <Icon style={{marginRight: 6, marginLeft: 6}} icon={`mdi mdi-${vehicleType?.icon}`} />
                                        </Row>

                                        <P>{`Stoptijd: ${route?.stopTime} s`}</P>
                                        {route?.customSpeed && route?.speedDifference !== 0 &&
                                            <P>{`Snelheid: ${Math.abs(route?.speedDifference || 0)}% ${route?.speedDifference > 0 ? 'langzamer' : 'sneller'}`}</P>
                                        }
                                    </Stack>

                                    <Stack style={{flex: 1, maxWidth: 175}}>
                                        {branchesAvailable(reseller) && route?.branchId &&
                                            <P ellipsis>{`${Languages.branch['NL']}: ${reseller.settings.branches?.find((branch) => branch.id === route?.branchId)?.name || '-'}`}</P>
                                        }
                                        <P ellipsis>{`${Languages.startPoint['NL']}: ${route?.startPoint?.street} ${route?.startPoint?.nr}${route?.startPoint?.addition}`}</P>
                                        <P ellipsis>{`${Languages.endPoint['NL']}: ${route?.endPoint?.street} ${route?.endPoint?.nr}${route?.endPoint?.addition}`}</P>
                                        {route?.rrule && (
                                            <Recurrence
                                                disabled
                                                rrule={route.rrule}
                                                iconStyle={{color: Colors.textDark}}
                                            />
                                        )}
                                    </Stack>
                                </Row>

                                {route?.instructions &&
                                    <Row style={{flexShrink: 0, marginTop: 12}}>
                                        <Icon style={{marginRight: 6}} icon='mdi mdi-message-outline' />

                                        <P ellipsis>{route?.instructions}</P>
                                    </Row>
                                }

                                <Row style={{flexShrink: 0, marginTop: 24, gap: 36}}>
                                    <Stack style={{flex: 1}}>
                                        <Row style={{flexShrink: 0}}>
                                            <Icon style={{marginRight: 6}} icon='mdi mdi-clock-outline' />

                                            <S1 ellipsis>{Languages.startTime['NL']}</S1>
                                        </Row>

                                        <P style={{marginLeft: 24}} ellipsis>Gepland</P>
                                        <P style={{marginLeft: 24}} ellipsis>Uitgevoerd</P>
                                    </Stack>

                                    <Stack style={{width: 40, alignItems: 'flex-end'}}>
                                        <S1 ellipsis>{route?.startTime ? formatDuration(moment(route?.startTime).startOf('minute').diff(moment(`${route.date} ${route?.plannedStartTime}`).startOf('minute'), 'seconds') || 0, true) : '--:--'}</S1>

                                        <P ellipsis>{route?.plannedStartTime || '-:--'}</P>

                                        <P ellipsis>{route?.startTime ? moment(route?.startTime).format('HH:mm') : '--:--'}</P>

                                    </Stack>

                                    <Stack style={{flex: 1}}>
                                        <Row style={{flexShrink: 0}}>
                                            <Icon style={{marginRight: 6}} icon='mdi mdi-clock-outline' />

                                            <S1 ellipsis>{Languages.endTime['NL']}</S1>
                                        </Row>

                                        <P style={{marginLeft: 24}} ellipsis>Gepland</P>
                                        <P style={{marginLeft: 24}} ellipsis>Uitgevoerd</P>
                                    </Stack>

                                    <Stack style={{width: 40, alignItems: 'flex-end'}}>
                                        <S1 ellipsis>{route?.endTime ? formatDuration(moment(route?.endTime).startOf('minute').diff(moment(route?.plannedEndTime).startOf('minute'), 'seconds') || 0, true) : '--:--'}</S1>

                                        <P ellipsis>{route?.plannedEndTime ? moment(route?.plannedEndTime).format('HH:mm') : '--:--'}</P>

                                        <P ellipsis>{route?.endTime ? moment(route?.endTime).format('HH:mm') : '--:--'}</P>

                                    </Stack>
                                </Row>

                                <Row style={{flexShrink: 0, marginTop: 24, gap: 36}}>
                                    <Stack style={{flex: 1}}>
                                        <Row style={{flexShrink: 0}}>
                                            <Icon style={{marginRight: 6}} icon='mdi mdi-timer-sand' />

                                            <S1 ellipsis>{Languages.distance['NL']}</S1>
                                        </Row>

                                        <P style={{marginLeft: 24}} ellipsis>Gepland</P>
                                        <P style={{marginLeft: 24}} ellipsis>Uitgevoerd</P>
                                    </Stack>

                                    <Stack style={{width: 40, alignItems: 'flex-end'}}>
                                        <S1 ellipsis>
                                            {route?.endTime ? formatDistance(route.completedDistance - route.plannedDistance, true) : '--'}
                                        </S1>

                                        <P ellipsis>{route?.plannedDistance ? formatDistance(route.plannedDistance) : '--'}</P>

                                        <P ellipsis>{route?.endTime && route?.completedDistance ? formatDistance(route.completedDistance) : '--'}</P>
                                    </Stack>

                                    <Stack style={{flex: 1}}>
                                        <Row style={{flexShrink: 0}}>
                                            <Icon style={{marginRight: 6}} icon='mdi mdi-timer-sand' />

                                            <S1 ellipsis>{Languages.duration['NL']}</S1>
                                        </Row>

                                        <P style={{marginLeft: 24}} ellipsis>Gepland</P>
                                        <P style={{marginLeft: 24}} ellipsis>Uitgevoerd</P>
                                    </Stack>

                                    <Stack style={{width: 40, alignItems: 'flex-end'}}>
                                        <S1 ellipsis>
                                            {route?.endTime ? formatDuration(delay, true) : '--:--'}
                                        </S1>

                                        <P ellipsis>{route?.plannedDuration ? formatDuration(route.plannedDuration) : '--:--'}</P>

                                        <P ellipsis>{route?.endTime ? formatDuration(actualDurationSec) : '--:--'}</P>
                                    </Stack>
                                </Row>

                                <Divider style={{marginBottom: 12}} />

                                <Row style={{flexShrink: 0, marginTop: 12}}>
                                    <Icon style={{marginRight: 6}} icon='mdi mdi-check-bold' />
                                    <P ellipsis style={{flex: 1}}>{Languages.completed['NL']}</P>
                                    <P ellipsis>{route?.success}</P>
                                </Row>

                                <Row style={{flexShrink: 0}}>
                                    <Icon style={{marginRight: 6}} icon='mdi mdi-close-thick' />
                                    <P ellipsis style={{flex: 1}}>{Languages.notCompleted['NL']}</P>
                                    <P ellipsis>{route?.notCompleted}</P>
                                </Row>

                                <Row style={{flexShrink: 0}}>
                                    <Icon style={{marginRight: 6}} icon='mdi mdi-clock-remove-outline' />
                                    <P ellipsis style={{flex: 1}}>{Languages.tooLate['NL']}</P>
                                    <P ellipsis>{route?.tooLate}</P>
                                </Row>

                                <Row style={{flexShrink: 0}}>
                                    <Icon style={{marginRight: 6}} icon='mdi mdi-home-outline' />
                                    <P ellipsis style={{flex: 1}}>{Languages.nrOfStops['NL']}</P>
                                    <P ellipsis>{stopBundles.countStops(route?.addresses || [])}</P>
                                </Row>

                                <Row style={{flexShrink: 0}}>
                                    <Icon style={{marginRight: 6}} icon='mdi mdi-package-variant-closed' />
                                    <P ellipsis style={{flex: 1}}>{Languages.nrOfColli['NL']}</P>
                                    <P ellipsis>{amountOfColli}</P>
                                </Row>

                                <Row style={{flexShrink: 0}}>
                                    <Icon style={{marginRight: 6}} icon='mdi mdi-currency-eur' />
                                    <P ellipsis style={{flex: 1}}>{`${Languages.revenue['NL']}${!route?.completed ? ` (${Languages.planned['NL']})` : ''}`}</P>

                                    <P ellipsis>{`€ ${formatPrice(route?.revenue || 0)}`}</P>
                                </Row>

                                <Row style={{flexShrink: 0}}>
                                    <Icon style={{marginRight: 6}} icon='mdi mdi-currency-eur' />
                                    <P ellipsis style={{flex: 1}}>{`${Languages.revenuePerHour['NL']}${!route?.completed ? ` (${Languages.planned['NL']})` : ''}`}</P>

                                    <P ellipsis>{`€ ${route?.revenuePerHour ? formatPrice(route?.revenuePerHour) : '-'}`}</P>
                                </Row>
                            </>
                        }
                    </Card>

                    {!route?.completed &&
                        <Row style={{flexShrink: 0, justifyContent: 'space-between', columnGap: formSpacing.columnGap, marginTop: 12}}>
                            <Button
                                style={{flex: 1}}
                                variant='outline-white'
                                tooltip={focusedRouteId === route?._id ? 'Reset focus' : 'Focus op kaart'}
                                icon={focusedRouteId === route?._id ? 'mdi mdi-image-filter-center-focus-weak' : 'mdi mdi-image-filter-center-focus-strong'}
                                onClick={() => OrderActions.setRouteFocus(route?._id)}
                            />

                            <Button
                                style={{flex: 1}}
                                variant='outline-white'
                                icon='mdi mdi-map-marker-path'
                                tooltip='Optimaliseren'
                                disabled={route?.loading || route?.ready}
                                onClick={() => OrderActions.planRoute(route._id, true)}
                            />

                            <Button
                                style={{flex: 1}}
                                variant='outline-white'
                                tooltip={route?.ready ? 'Route wijzigen' : 'Gereed'}
                                icon={route?.ready ? 'mdi mdi-pencil' : 'mdi mdi-check'}
                                disabled={route?.loading}
                                onClick={async () => {
                                    if (route.ready) {
                                        if (!await this.popup.open('Route wijzigen', 'Weet je zeker dat je deze route wilt wijzigen? ')) return
                                    } else {
                                        const uniqueColli = route.colli?.filter((c, index, self) => c.barcode ? index === self.findIndex((collo) => collo.barcode === c.barcode) : true)

                                        const amountOfColli = uniqueColli?.length || 0

                                        const vehicleType = reseller.settings.vehicleTypes.find((v) => v._id === route.vehicleTypeId)

                                        const vehicle = vehicles.find((vehicle) => vehicle._id === route.vehicleId)

                                        let tooMuchColli = false

                                        vehicleType.payloads?.forEach((payload) => {
                                            if (tooMuchColli) {
                                                return
                                            }

                                            if (payload.payloadType === 'colli') {
                                                if (payload.payloadColliType) {
                                                    const amountOfColliType = uniqueColli?.filter((collo) => collo.type === payload.payloadColliType).length
                                                    if (payload.payload > 0 && amountOfColliType > payload.payload) {
                                                        tooMuchColli = payload.payloadColliType
                                                    }
                                                } else if (payload.payload > 0 && amountOfColli > payload.payload) {
                                                    tooMuchColli = true
                                                }
                                            }
                                        })

                                        let totalWeight = 0

                                        uniqueColli?.forEach((collo) => {
                                            totalWeight += collo.weight
                                        })

                                        const tooHeavy = _.some(vehicleType.payloads, (payload) => {
                                            if (payload.payloadType === 'weight') {
                                                return payload.payload > 0 && totalWeight > payload.payload
                                            }
                                        })

                                        const tooFar = vehicleType?.range && vehicleType.range > 0 && route.distance > vehicleType.range

                                        const vehicleRoutes = Object.values(routes).filter((r) => vehicle && r.vehicleId === vehicle?._id && r._id !== route._id && (!r.completed && !route.completed))

                                        const thisRouteStart = route.startTime || (route.plannedStartTime ? `${route.date} ${route.plannedStartTime}` : undefined)
                                        const thisRouteEnd = route.endTime || route.estimatedEndTime

                                        const routeOverlaps = vehicleRoutes.some((r) => {
                                            const startTime = r.startTime || (r.plannedStartTime ? `${r.date} ${r.plannedStartTime}` : undefined)
                                            const endTime = r.endTime || r.estimatedEndTime

                                            return moment(thisRouteStart).isBetween(moment(startTime), moment(endTime), 'minute', '[)') || moment(thisRouteEnd).isBetween(moment(startTime), moment(endTime), 'minute', '(]')
                                        })

                                        const messengerRoutes = Object.values(routes).filter((r) => route.messengerId && r.messengerId === route.messengerId && r._id !== route._id && (!r.completed && !route.completed))

                                        const messengerOverlaps = messengerRoutes.some((r) => {
                                            const startTime = r.startTime || (r.plannedStartTime ? `${r.date} ${r.plannedStartTime}` : undefined)
                                            const endTime = r.endTime || r.estimatedEndTime

                                            return moment(thisRouteStart).isBetween(moment(startTime), moment(endTime), 'minute', '[)') || moment(thisRouteEnd).isBetween(moment(startTime), moment(endTime), 'minute', '(]')
                                        })

                                        const warnings = []
                                        if (tooMuchColli) {
                                            warnings.push('Deze route bevat meer colli dan het laadvermogen van het geselecteerde voertuig.')
                                        }

                                        if (tooHeavy) {
                                            warnings.push('Deze route bevat colli die zwaarder zijn dan het laadvermogen van het geselecteerde voertuig.')
                                        }

                                        if (tooFar) {
                                            warnings.push('Deze route is langer dan het bereik van het geselecteerde voertuig.')
                                        }

                                        if ((route.plannedStartTime || route.startTime) && !getRouteTimeSlots(route).inTimeSlots) {
                                            warnings.push('Deze route haalt niet alle stops binnen het aangegeven tijdvak.')
                                        }

                                        if (routeOverlaps) {
                                            warnings.push('Deze route heeft een dubbel geboekt voertuig.')
                                        }

                                        if (messengerOverlaps) {
                                            warnings.push('Deze route heeft een dubbel geboekte koerier.')
                                        }

                                        if (warnings.length) {
                                            this.popup.setWarnings(warnings)
                                            if (!await this.popup.open('Route gereed maken', 'Weet je zeker dat je door wil gaan?')) return
                                        }
                                    }

                                    OrderActions.setRouteReady(route._id, !route.ready, (err) => {
                                        if (err) {
                                            this.popup.setError(err)
                                        } else {
                                            this.popup.close()
                                        }
                                    })
                                }}
                            />

                            <Popover
                                placement='left'
                                // The Popover doesn't take its child's flex, the wrapper has to grow like the button
                                style={{flex: 1}}
                                content={
                                    <div style={{width: 'fit-content'}}>
                                        <ListItem
                                            size='sm'
                                            icon={route?.hidden ?
                                                'mdi mdi-map-marker' :
                                                'mdi mdi-map-marker-off'
                                            }
                                            onClick={() => OrderActions.setRouteHidden(route._id)}
                                        >

                                            {route?.hidden ? 'Tonen op kaart' : 'Verbergen op kaart'}
                                        </ListItem>

                                        <ListItem
                                            size='sm'
                                            icon='mdi mdi-tune'
                                            onClick={() => this.props.onEditRoute(route)}
                                        >
                                            Route instellingen
                                        </ListItem>

                                        <ListItem
                                            size='sm'
                                            icon='mdi mdi-swap-vertical'
                                            disabled={route?.loading || route?.ready || !route?.reversible || route?.anchorsEnabled}
                                            onClick={() => OrderActions.planRoute(route._id, false, true)}
                                        >
                                            Volgorde omdraaien
                                        </ListItem>

                                        <ListItem
                                            size='sm'
                                            icon={route?.anchorsEnabled ? 'mdi mdi-anchor-off' : 'mdi mdi-anchor'}
                                            disabled={route?.loading || route?.ready}
                                            onClick={() => {
                                                OrderActions.setRouteAnchoring(route._id, !route?.anchorsEnabled, (err) => {
                                                    if (err) {
                                                        this.popup.setError(err)
                                                    }
                                                })
                                            }}
                                        >
                                            {route?.anchorsEnabled ? 'Vastzetten begin/eind opheffen' : 'Vastzetten begin/eind'}
                                        </ListItem>

                                        <ListItem
                                            size='sm'
                                            icon='mdi mdi-calendar'
                                            disabled={route?.loading}
                                            onClick={async () => {
                                                this.popup.setInput({type: 'date', value: time.tomorrow(), label: 'Datum'})
                                                const date = await this.popup.open('Route verplaatsen', 'Weet je zeker dit zeker?')
                                                if (date === undefined) return

                                                OrderActions.rescheduleRoute(route._id, date, (err) => {
                                                    if (err) {
                                                        this.popup.setError(err)
                                                    } else {
                                                        this.popup.close()
                                                    }
                                                })
                                            }}
                                        >
                                            Verplaatsen naar datum
                                        </ListItem>

                                        <ListItem
                                            size='sm'
                                            icon='mdi mdi-flag-checkered'
                                            disabled={!route?.completedAddresses?.length || route?.completed}
                                            onClick={async () => {
                                                if (route.addresses?.length) {
                                                    if (!await this.popup.open('Route afronden', 'Niet afgeronde stops worden uit de route verwijderd. Weet je zeker dat je deze route wilt afronden?')) return
                                                } else {
                                                    if (!await this.popup.open('Route afronden', 'Weet je zeker dat je deze route wilt afronden?')) return
                                                }

                                                OrderActions.stopRoute(route._id, (err) => {
                                                    if (err) {
                                                        this.popup.setError(err)
                                                    } else {
                                                        this.popup.close()
                                                    }
                                                })
                                            }}
                                        >
                                            Afronden
                                        </ListItem>

                                        <ListItem
                                            style={{color: route?.ready || route?.started || route?.completed || route?.completedAddresses?.length ? undefined : Colors.errorBright}}
                                            size='sm'
                                            icon='mdi mdi-delete'
                                            disabled={route?.loading || route?.ready || route?.started || route?.completed || route?.completedAddresses?.length}
                                            onClick={async () => {
                                                if (route.templateId) {
                                                    if (!await this.popup.open('Template route verwijderen', 'Je gaat een route verwijderen die is aangemaakt vanuit route templates. Deze is niet meer terug te halen. Weet je zeker dat je door wil gaan?')) return
                                                } else if (route.addresses?.length) {
                                                    if (!await this.popup.open('Route verwijderen', 'Deze route bevat stops, weet je zeker dat je deze route wilt verwijderen?')) return
                                                }

                                                OrderActions.removeRoute(route._id, (err) => {
                                                    if (err) {
                                                        this.popup.setError(err)
                                                    } else {
                                                        this.popup.close()
                                                    }
                                                })
                                            }}
                                        >
                                            Verwijderen
                                        </ListItem>
                                    </div>
                                }
                            >
                                <Button
                                    style={{flex: 1}}
                                    variant='outline-white'
                                    icon='mdi mdi-dots-vertical'
                                />
                            </Popover>
                        </Row>
                    }

                    {route?.completed &&
                        <Row style={{flexShrink: 0, justifyContent: 'center', columnGap: formSpacing.columnGap}}>
                            <Button
                                style={{flex: 1}}
                                variant='outline-white'
                                tooltip={route.hidden ? 'Toon route' : 'Verberg route'}
                                icon={route.hidden ?
                                    'mdi mdi-map-marker' :
                                    'mdi mdi-map-marker-off'
                                }
                                onClick={() => OrderActions.setRouteHidden(route._id)}
                            />

                            <Button
                                style={{flex: 1}}
                                variant='outline-white'
                                tooltip={focusedRouteId === route?._id ? 'Reset focus' : 'Focus route'}
                                icon={focusedRouteId === route?._id ? 'mdi mdi-image-filter-center-focus-weak' : 'mdi mdi-image-filter-center-focus-strong'}
                                onClick={() => OrderActions.setRouteFocus(route?._id)}
                            />

                            {localStorage.admin &&
                                <Button
                                    style={{flex: 1}}
                                    variant='outline-white'
                                    icon='mdi mdi-cached'
                                    tooltip='Route duur herberekenen'
                                    disabled={route?.loading}
                                    onClick={() => OrderActions.planRoute(route._id)}
                                />
                            }
                        </Row>
                    }

                    {!route?.ready && !route?.completed && _.some(route?.addresses, (address) => address.locked) &&
                        <Row style={{flexShrink: 0, justifyContent: 'center'}}>
                            <Button
                                variant='text'
                                label={t('unlockAllStopNumbers')}
                                icon='mdi mdi-lock-open-variant-outline'
                                disabled={route?.loading}
                                onClick={() => OrderActions.setRouteLocked(route._id, false)}
                            />
                        </Row>
                    }

                    {/* Above the addresses and sticky, so the ETA stays in view while scrolling through them */}
                    <NextStopETA route={route} style={{position: 'sticky', top: 0, zIndex: 1, margin: '12px 0 0', width: '100%'}} />

                    <SortableList
                        style={{marginTop: 12}}
                        items={stopItems}
                        onChange={this.onDragChange.bind(this)}
                        isDisabled={(item) => route?.loading || item.hasArrived || (item.isBundle ? item.addresses.some((address) => address.locked) : item.locked)}
                        renderItem={(item) => {
                            if (isAnchorLine(item)) {
                                return (
                                    <Row style={{flexShrink: 0, alignItems: 'center', gap: 6, height: 36, marginBottom: 6, width: '100%', cursor: 'grab'}}>
                                        <div style={{flex: 1, height: 2, background: Colors.textMedium}} />

                                        <Icon size='md' style={{marginRight: 6, color: Colors.textMedium, cursor: 'grab'}} icon='mdi mdi-anchor' />

                                        <Icon size='md' style={{marginRight: 6, color: Colors.textMedium, cursor: 'grab'}} icon='mdi mdi-drag-horizontal' />

                                        <div style={{flex: 1, height: 2, background: Colors.textMedium}} />
                                    </Row>
                                )
                            }

                            return item.isBundle ? this.renderBundle(item) : this.renderStop(item)
                        }}
                    />
                </div>

                <Popup ref={(ref) => this.popup = ref} />
            </Stack>
        )
    }
}

export default withVehiclesHook(RoutePanel)
